import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TriangleAlertIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { signOut } from "@/features/auth/services/firebase";
import { deleteAccount, exportUserData } from "@/services/api-client";
import { downloadBlob } from "@/utils/blob-download";
import { resolveApiErrorMessage } from "@/utils/api-error-message";

/**
 * Settings → Account → Danger zone: GDPR data export (SAR) plus irreversible
 * account deletion (right to erasure), gated behind an explicit confirm
 * dialog. Both apply to every account regardless of auth provider — they
 * wire the existing `/api/gdpr/*` endpoints (no client-side deletion). On a
 * successful delete the user is signed out and hard-redirected to login to
 * guarantee no stale authenticated state remains.
 */
export default function SettingsDangerZone() {
  const { t } = useTranslation(["settings", "common"]);

  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleExport = async () => {
    setExportStatus(null);
    setExportError(null);
    setExporting(true);
    try {
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `vardic-data-export-${date}.json`);
      setExportStatus(t("settings:success.dataDownloaded"));
    } catch (err) {
      setExportError(resolveApiErrorMessage(err, "settings:errors.downloadDataFailed", t));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteAccount();
    } catch (err) {
      // Only a failed erasure shows an error; sign-out below is best-effort.
      setDeleting(false);
      setDeleteError(resolveApiErrorMessage(err, "settings:errors.deleteAccountFailed", t));
      return;
    }
    // Erasure succeeded and is irreversible: sign out and hard-redirect so
    // no in-memory authenticated state (auth context, active team, caches)
    // survives. A sign-out rejection (network blip, Firebase token error)
    // must NOT surface as "delete failed" - the data is already gone and a
    // retry would only hit the backend's once-per-day erasure limit.
    try {
      await signOut();
    } catch {
      // Redirect regardless; the hard navigation clears client state.
    }
    // Intentionally do not clear `deleting` — we are leaving.
    window.location.assign("/login");
  };

  return (
    <>
      <section
        className="space-y-6 rounded-lg border border-[color:color-mix(in_srgb,var(--destructive)_28%,var(--border))] bg-[var(--card)] p-6"
        data-test="settings-danger-zone"
      >
        <h2 className="text-lg font-semibold text-[var(--foreground)]">
          {t("settings:security.dangerZone")}
        </h2>

        <div className="space-y-3">
          <p className="text-sm text-[var(--muted-foreground)]">
            {t("settings:security.downloadDataHint")}
          </p>
          {exportStatus && (
            <Alert data-test="settings-export-status" variant="success">
              <AlertDescription>{exportStatus}</AlertDescription>
            </Alert>
          )}
          {exportError && (
            <Alert data-test="settings-export-error" variant="error">
              <AlertDescription>{exportError}</AlertDescription>
            </Alert>
          )}
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-test="settings-download-data"
          >
            {t("settings:security.downloadData")}
          </Button>
        </div>

        <div className="space-y-3 border-t border-[var(--border)] pt-6">
          <p className="text-sm text-[var(--muted-foreground)]">
            {t("settings:security.deleteAccountWarning")}
          </p>
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteError(null);
              setDeleteDialogOpen(true);
            }}
            data-test="settings-delete-account"
          >
            {t("settings:security.deleteAccount")}
          </Button>
        </div>
      </section>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          // Block dismissal while the delete request is in flight.
          if (!open && !deleting) setDeleteDialogOpen(false);
        }}
      >
        <AlertDialogPopup data-test="settings-delete-account-dialog">
          <AlertDialogHeader>
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/8 text-destructive-foreground">
              <TriangleAlertIcon aria-hidden="true" />
            </div>
            <AlertDialogTitle>
              {t("settings:security.deleteAccountConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("settings:security.deleteAccountWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteError && (
            <div
              className="mx-6 mb-4 rounded-md border border-destructive/32 bg-destructive/8 px-3 py-2 text-destructive-foreground text-sm"
              data-test="settings-delete-account-error"
              role="alert"
            >
              {deleteError}
            </div>
          )}

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              className="flex-1"
              data-test="settings-delete-account-cancel"
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1"
              data-test="settings-delete-account-confirm"
            >
              {t("settings:security.deleteAccount")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
